// src/components/users/useUsers.js
import { useState, useEffect } from 'react';
import api from '../../api';
import { useAuth } from '../../authContext';

export const useUsers = () => {
    const { token } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Benutzer laden ---
    const fetchUsers = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await api.get('/users', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(response.data || []);
            setError(null);
        } catch (err) {
            setError('Fehler beim Laden der Benutzer. Bitte versuchen Sie es später erneut.');
            console.error('Fehler beim Laden der Benutzer:', err);
        } finally {
            setLoading(false);
        }
    };

    // Initiales Laden der Benutzer
    useEffect(() => {
        fetchUsers();
    }, [token]);

    // --- Benutzer hinzufügen ---
    const addUser = async (userData) => {
        try {
            const response = await api.post(
                '/users/register',
                {
                    username: userData.username,
                    passwordHash: userData.password, // Backend hasht das Passwort
                    email: userData.email,
                    role: userData.role,
                    active: true, // Standardmäßig aktiv
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Benutzer zur lokalen Liste hinzufügen
            const newUser = {
                ...userData,
                id: response.data.id,
                active: true,
            };
            setUsers([...users, newUser]);
            return newUser;
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Fehler beim Erstellen des Benutzers.';
            throw new Error(errorMessage);
        }
    };

    // --- Benutzer bearbeiten ---
    const editUser = async (userId, userData) => {
        try {
            // Passwort nur senden, wenn es geändert wurde
            const payload = {
                username: userData.username,
                email: userData.email,
                role: userData.role,
                active: userData.active,
            };

            // Passwort nur hinzufügen, wenn es im Formular ausgefüllt wurde
            if (userData.password) {
                payload.passwordHash = userData.password;
            }

            await api.put(
                `/users/${userId}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Lokale Liste aktualisieren
            setUsers(
                users.map((user) =>
                    user.id === userId
                        ? {
                            ...user,
                            username: userData.username,
                            email: userData.email,
                            role: userData.role,
                            active: userData.active,
                        }
                        : user
                )
            );
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Fehler beim Bearbeiten des Benutzers.';
            throw new Error(errorMessage);
        }
    };

    // --- Benutzer-Status ändern (Aktiv/Inaktiv) ---
    const toggleUserStatus = async (userId, currentStatus) => {
        try {
            await api.put(
                `/users/${userId}/active?isActive=${!currentStatus}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Lokale Liste aktualisieren
            setUsers(
                users.map((user) =>
                    user.id === userId ? { ...user, active: !currentStatus } : user
                )
            );
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Fehler beim Ändern des Status.';
            throw new Error(errorMessage);
        }
    };

    // --- Backup-Funktion ---
    const getDatabaseBackup = async () => {
        if (!token) {
            throw new Error('Kein Token verfügbar. Bitte melden Sie sich an.');
        }
        try {
            const response = await api.get('/common/backup', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'text/plain',
                },
            });
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Fehler beim Abrufen des Backups.';
            throw new Error(errorMessage);
        }
    };

    return {
        users,
        loading,
        error,
        fetchUsers,
        addUser,
        editUser,
        toggleUserStatus,
        getDatabaseBackup, // Neue Methode
    };
};

export default useUsers;